<script lang="ts" setup>
  import { computed, ref, watch } from 'vue';
  import { useVariantStock, addToCartEvent, removeFromCartEvent, useGoogleTagManager } from '@storefront/storefront';
  import { closestMultipleOf, ProductType } from '@othercommerce/domain';
  import type { OrderItemWithStock } from '@storefront/resources';
  import { useHttpClient, useShared, useRoute } from '@othercommerce/foundation';
  import { Features } from '@shared/features';

  interface Props {
    item: OrderItemWithStock;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const http = useHttpClient();
  const shared = useShared();
  const gtm = useGoogleTagManager();

  const stockUnitBase = computed(() => {
    if (props.item) {
      return props.item.stockUnitBase;
    } else {
      return '1.0000';
    }
  });

  const deleteLoading = ref(false);
  const amountLoading = ref(false);
  const amountThrottle = ref();
  const amount = ref(Number(props.item.amount));
  const { maxAmountAvailable } = useVariantStock(props.item);

  function onAmountChange(event: InputEvent, item: OrderItemWithStock) {
    clearTimeout(amountThrottle.value);

    amountThrottle.value = setTimeout(() => {
      let target = event.target as HTMLInputElement;

      if (!target) {
        return;
      }

      amountLoading.value = true;

      let quantity = Number(target.value);
      let step = Number(item.stockUnitBase);

      const requestedAmount = amount.value - Number(props.item.amount);
      amount.value = closestMultipleOf(quantity, step);


      http.patch(route('web.cart.item.update', { item: item.id }), { amount: amount.value }).then(() => {
        if (requestedAmount > 0) {
          gtm.push(addToCartEvent(item, requestedAmount, shared.value.activeCurrencyCode));
        } else {
          gtm.push(removeFromCartEvent(item, requestedAmount, shared.value.activeCurrencyCode));
        }
        amountLoading.value = false;
      });
    }, 750);
  }

  function onDestroy(item: OrderItemWithStock) {
    if (deleteLoading.value) return;
    deleteLoading.value = true;

    http.delete(route('web.cart.item.destroy', { item: item.id })).then(() => {
      gtm.push(removeFromCartEvent(item, amount.value, shared.value.activeCurrencyCode));
      deleteLoading.value = false;
    }).catch(() => {
      deleteLoading.value = false;
    });
  }

  const target = computed(() => {
    return route('web.product.show', { slug: props.item.variantSlug });
  });

  const promotion = computed(() => {
    if (props.item.regularGross && props.item.priceGross) {
      return props.item.regularGross !== props.item.priceGross;
    }

    return false;
  });

  const daysUntilDelivery = computed(() => {
    const deliveryDay = shared.value.currentPickupPoint?.closestDeliveryDay?.value;

    if (!deliveryDay) {
      return null;
    }

    const today = new Date().getDay();
    const currentDay = today === 0 ? 7 : today;

    return (+deliveryDay - currentDay + 7) % 7 || 7;
  });

  const deliveryMessage = computed(() => {
    const deliveryDay = shared.value.currentPickupPoint?.closestDeliveryDay;

    if (!deliveryDay || !daysUntilDelivery.value) {
      return null;
    }

    return {
      days: +daysUntilDelivery.value,
      dayName: deliveryDay.label,
    };
  });

  watch(() => props.item, (item) => {
    amount.value = Number(item.amount);
  });
</script>
<template>
  <SfListCard class="cart-item">
    <template #image>
      <MediaPicture :photo="item.photo" class="cart-item__photo" sizes="240x240 2x, 120x120" />
    </template>
    <template #actions>
      <div class="cart-item__price-wrapper">
        <SfProductCatalogPrice :item="item" />
        <SfProductPrice :price="item" class="cart-item__price" row />
        <SfProductOmnibus :price="item" />
      </div>
      <div class="cart-item__buttons">
        <SfWishlistButton :variant="item.variantId" :variantModel="item" />
        <SfButton :aria-label="$t('SHOP.CART.ARIA.REMOVE_FROM_CART')" :disabled="deleteLoading" :loading="deleteLoading" danger square transparent @click="onDestroy(item)" @keydown.enter.stop="onDestroy(item)">
          <template #after>
            <SfIconTrash aria-hidden="true" />
          </template>
        </SfButton>
      </div>
    </template>
    <template #default>
      <header class="cart-item__header">
        <RouterLink :href="target">
          <h2 class="cart-item__title">
            {{ item.variantTitle }}
          </h2>
        </RouterLink>
        <p v-if="item.variantSku" class="cart-item__sku">
          {{ $t('SHOP.CART.LABEL.SKU') + ' ' + item.variantSku }}
        </p>
      </header>
      <SfProductStock v-if="item.productType !== ProductType.FLEXIBLE" :variant="item" class="cart-item__stock" />
      <div v-if="item.components.length > 0" class="cart-item__components">
        <CartItemComponent v-for="component in item.components" :component />
      </div>
      <div v-if="item.productType !== ProductType.FLEXIBLE" class="cart-item__amount">
        <SfFormNumber v-model="amount" :label="$t('SHOP.CART.LABEL.QUANTITY')" :loading="amountLoading" :max="maxAmountAvailable" :min="stockUnitBase" :step="stockUnitBase" @change="onAmountChange($event, item)" />
      </div>
      <div v-if="!item.stockOnOrder && amount > Number(item.stockMeta.current)" class="cart-item__stock__warning">
        <p v-html="$t('SHOP.CART.MESSAGE.CART_ITEM_STOCK_WARNING')" />
        <p v-if="deliveryMessage">
          {{ $tc('SHOP.CART.MESSAGE.CART_ITEM_REALIZATION', deliveryMessage.days, deliveryMessage) }}
        </p>
      </div>
      <div v-if="item.isMedicinalProduct && Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled" class="cart-item__medicinal-warning">
        <span v-html="$t('SHOP.CART.MESSAGE.CART_ITEM_MEDICINAL_WARNING')" />
      </div>
    </template>
    <template #icons>
      <SfProductTag :visible="promotion" tag-type="discount">
        {{ $t('SHOP.COMMON.LABEL.SALE') }}
      </SfProductTag>
    </template>
  </SfListCard>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-cart-item-trnasition-duration: 200ms;
    --sf-cart-item-trnasition-timing-func: ease-out;
    --sf-cart-item-title-color: rgb(0, 0, 0,);
    --sf-cart-item-sku-color: var(--color-secondary);
    --sf-cart-item-title-color-hover: rgb(70, 70, 70);

    --sf-cart-item-border-color: rgb(240, 240, 240);
    --sf-cart-item-border-width: 1px;
    --sf-cart-item-border-style: solid;

    --sf-cart-item-box-shadow: none;

    --sf-cart-item-components-padding-x: 0;
    --sf-cart-item-components-padding-y: 0.5rem;

    --sf-cart-item-components-gap-x: 0.5rem;
    --sf-cart-item-components-gap-y: 0.75rem;
  }

  .cart-item {
    --sf-card-border-color: var(--sf-cart-item-border-color);
    --sf-card-border-width: var(--sf-cart-item-border-width);
    --sf-card-border-style: var(--sf-cart-item-border-style);
    --sf-card-box-shadow: var(--sf-cart-item-box-shadow);
    --sf-product-price-current-font-size: 1.25rem;
    --sf-product-price-gap-row: 0.5rem;

    &__title {
      font-size: 1.25rem;
      color: var(--sf-cart-item-title-color);
      transition-property: text-decoration-color;
      transition-duration: var(--sf-cart-item-trnasition-duration);
      transition-timing-function: var(--sf-cart-item-trnasition-timing-func);
      cursor: pointer;
      text-decoration: underline;
      text-decoration-color: transparent;
      word-break: break-word;

      @media(hover: hover) {
        &:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    &__sku {
      font-size: 0.875rem;
      color: var(--sf-cart-item-sku-color);
    }

    &__components {
      display: grid;
      min-width: 0;
      width: 100%;
      padding: var(--sf-cart-item-components-padding-y) var(--sf-cart-item-components-padding-x);
      gap: var(--sf-cart-item-components-gap-y) var(--sf-cart-item-components-gap-x);
    }

    &__stock {
      .stock__icon {
        display: none;
      }

      &__warning {
        font-size: 0.75rem;
        --font-size: 0.75rem;
        color: var(--sf-status-color-warning);
        background-color: var(--sf-status-background-warning);
        border: 1px solid var(--sf-status-color-warning);
        border-radius: var(--border-radius);
        padding: 0.25rem 0.5rem;
        font-weight: var(--font-weight-semi);
      }
    }

    &__price-wrapper, .price {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem
    }

    .price {
      gap: 0.25rem
    }

    .omnibus {
      max-width: 140px;
    }

    &__amount {
      display: flex;
      justify-content: flex-start;

      .control-wrapper {
        width: min-content;
      }
    }

    &__buttons {
      display: flex;
      gap: 0.5rem;
      font-size: 1.125rem;
    }

    &__medicinal-warning {
      font-size: 0.75rem;
    }


    .list-card__image {
      flex: 0 0 120px;
    }

    @include media.query(lg) {

      &__title:hover {
        box-shadow: none
      }

      .list-card__actions {
        gap: 0.5rem
      }
      .omnibus {
        max-width: 260px;
      }
    }
  }

  @include media.query(md) {
    .cart-item {
      --sf-list-card-gap: 0.75rem;
      --sf-product-price-current-font-size: 1rem;

      &__amount {
        margin-top: 0;
      }

      &__buttons {
        margin-top: 0.75rem;
      }

      &__title {
        font-size: 0.875rem;
      }

      &__sku {
        font-size: 0.75rem;
      }

      .list-card__wrapper {
        padding: 0.75rem 0.5rem;

        .list-card__image {
          flex: 0 0 70px;
        }

        .list-card__actions-content {
          gap: 0.75rem;
          flex-direction: row-reverse;
        }

        .list-card__content-default {
          gap: 0.75rem;
        }
      }

      .cart-item__buttons {
        margin-top: 0;
      }
    }
  }
</style>
